/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import { track } from '@coursera/event-pulse/sdk';

import ActiveSubscriptions from 'bundles/composable-page-header/components/ActiveSubscriptions';
import LanguageTranslationMenuItem from 'bundles/composable-page-header/components/mobile-menu-items/LanguageTranslationMenuItem';
import MobileButton from 'bundles/composable-page-header/components/mobile-menu-items/MobileButton';
import MobileLogOutButton from 'bundles/composable-page-header/components/mobile-menu-items/MobileLogOutButton';
import { useCloseMobileMenu } from 'bundles/composable-page-header/contexts/MobileNavigationContext';
import type { AccountDropdownLink } from 'bundles/composable-page-header/types';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  dropdownLinks: Array<AccountDropdownLink>;
  courseraPlusSubscriptionCycle?: string | null;
  hideLanguage?: boolean;
};

const styles = {
  yourAccount: css`
    padding-top: var(--cds-spacing-200);
    padding-bottom: var(--cds-spacing-50);
    padding-left: var(--cds-spacing-100);
  `,
  ActiveSubscriptionsBorder: css`
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    margin: var(--cds-spacing-50) 0;
  `,
};

const AccountSubMenu = ({ dropdownLinks, hideLanguage, courseraPlusSubscriptionCycle }: Props) => {
  const closeMenu = useCloseMobileMenu();
  const isOrgScoped = GrowthDiscoveryEpicClient.preview('orgScopedHome');

  const linkItemClick = (link: AccountDropdownLink) => {
    track('click_profile_menu_item', {
      profileItemName: link.name ?? '',
      profileItemLink: link.href,
    });
    closeMenu?.();
  };

  const mobileMenuItems = dropdownLinks.reduce<React.ReactNode[]>((acc, link) => {
    acc.push(
      <MobileButton
        key={link.name}
        href={link.href}
        label={link.label}
        name={link.name}
        onButtonClick={() => linkItemClick(link)}
      />
    );

    if (!isOrgScoped && link.name === 'account-settings' && !hideLanguage) {
      acc.push(<LanguageTranslationMenuItem key="language" />);
    }
    if (isOrgScoped && link.name === 'learner-help-center' && !hideLanguage) {
      acc.push(<LanguageTranslationMenuItem key="language" />);
    }

    return acc;
  }, []);

  return (
    <div>
      {!isOrgScoped && (
        <Typography2 component="h3" variant="subtitleLarge" css={styles.yourAccount}>
          {_t('Your Account')}
        </Typography2>
      )}
      {!!courseraPlusSubscriptionCycle && (
        <div css={styles.ActiveSubscriptionsBorder}>
          <ActiveSubscriptions
            title={_t('Your current subscription:')}
            subtitle={
              courseraPlusSubscriptionCycle === 'MONTHLY' ? _t('Coursera Plus Monthly') : _t('Coursera Plus Annual')
            }
          />
        </div>
      )}
      {[...mobileMenuItems, <MobileLogOutButton tabIndex={0} key="signOut" />]}
    </div>
  );
};

export default AccountSubMenu;
